import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../features/admin/components/Sidebar';
import Dashboard from '../features/admin/components/Dashboard';
import OrdersView from '../features/admin/components/OrdersView';
import ProductsView from '../features/admin/components/products/ProductsView';
import CustomersView from '../features/admin/components/customers/CustomersView';
import OrderDetailsModal from '../features/admin/components/OrderDetailsModal';
import StockCheckModal from '../features/admin/components/StockCheckModal';
import OrderDetailDrawer from '../features/admin/components/shared/OrderDetailDrawer';
import CancelOrderModal from '../features/admin/components/shared/CancelOrderModal';
import PosView from '../features/admin/components/PosView';
import { orderService } from '../services';
import { getAllOrders, getWorkflowByPlan } from '../services/api/salesOrderApi';
import { getCurrentTenant } from '../services/api/companyApi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import MobileNavbar from '../features/admin/components/MobileNavbar';
import StaffView from '../features/admin/components/StaffView';
import SettingsView from '../features/admin/components/settings/SettingsView';
import CompaniesView from '../features/admin/components/superadmin/CompaniesView';
import CompanyDetailView from '../features/admin/components/superadmin/CompanyDetailView';
import SubscriptionPlansView from '../features/admin/components/superadmin/SubscriptionPlansView';
import SystemAdminDashboard from '../features/admin/components/superadmin/SystemAdminDashboard';
import SystemAnalyticsView from '../features/admin/components/superadmin/SystemAnalyticsView';
import SalesOrdersView from '../features/admin/components/sales/SalesOrdersView';
import SalesTargetsView from '../features/admin/components/sales/SalesTargetsView';
import WarehouseRecentOrders from '../features/admin/components/warehouse/WarehouseRecentOrders';
import WarehouseConfirmedView from '../features/admin/components/warehouse/WarehouseConfirmedView';
import { normalizeRole, ROLES } from '../config/roles';
import TopHeader from '../features/admin/components/shared/TopHeader';
import AnalyticsView from '../features/admin/components/analytics/AnalyticsView';
import SalesPersonAnalyticsView from '../features/admin/components/analytics/SalesPersonAnalyticsView';
import DailySalesReportView from '../features/admin/components/sales/DailySalesReportView';
import DailyReportsView from '../features/admin/components/reports/DailyReportsView';
import OperationsAnalyticsView from '../features/admin/components/reports/OperationsAnalyticsView';
import ProfileView from '../features/admin/components/ProfileView';
import WarehouseManagementContainer from '../features/admin/components/warehouse/management/WarehouseManagementContainer';

const AdminPage = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const handleLogout = async () => {
        await logout();
        sessionStorage.removeItem('activeView');
        sessionStorage.removeItem('selectedRole');
        sessionStorage.removeItem('selectedCompany');
        navigate('/login');
    };

    // -------------------------------------------------------------------------
    // Role resolution
    // availableRoles and currentRole are ALWAYS canonical Keycloak role codes
    // (e.g. 'SALES_PERSON', 'WAREHOUSE_PERSON'). Display names are handled
    // in the Sidebar via getRoleDescription().
    // -------------------------------------------------------------------------
    const availableRoles = React.useMemo(() => {
        if (!user) return ['SALES_PERSON'];

        const allRawRoles = [...(user.roles || []), ...(user.realmRoles || []), ...(user.rawRoles || [])];
        if (allRawRoles.length === 0) return ['SALES_PERSON'];

        const roleCodes = new Set();
        allRawRoles.forEach(raw => {
            const code = normalizeRole(raw);
            if (code && code !== 'USER') roleCodes.add(code);
        });

        const arr = Array.from(roleCodes);
        // Sort by privilege precedence
        const precedence = Object.keys(ROLES);
        arr.sort((a, b) => {
            const ia = precedence.indexOf(a);
            const ib = precedence.indexOf(b);
            return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
        });

        return arr.length > 0 ? arr : ['SALES_PERSON'];
    }, [user]);

    const [currentRole, setCurrentRole] = useState(() => {
        const saved = sessionStorage.getItem('selectedRole');
        // Normalize any saved legacy value on restore
        const normalized = normalizeRole(saved) || saved;
        return normalized || 'SALES_PERSON';
    });

    useEffect(() => {
        if (availableRoles.length > 0 && !availableRoles.includes(currentRole)) {
            setCurrentRole(availableRoles[0]);
            sessionStorage.setItem('selectedRole', availableRoles[0]);
        }
    }, [availableRoles, currentRole]);

    const handleRoleChange = (role) => {
        setCurrentRole(role);
        sessionStorage.setItem('selectedRole', role);
        setActiveView('dashboard');
    };

    const [activeView, setActiveView] = useState(() => {
        const saved = sessionStorage.getItem('activeView') || 'dashboard';
        // SALES_PERSON should never land on daily-reports (the read-only report page).
        // Redirect stale sessionStorage values to the correct operations log view.
        const savedRole = normalizeRole(sessionStorage.getItem('selectedRole'));
        if (saved === 'daily-reports' && savedRole === 'SALES_PERSON') {
            return 'sales-report';
        }
        return saved;
    });

    useEffect(() => {
        sessionStorage.setItem('activeView', activeView);
    }, [activeView]);

    const [orders, setOrders] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [stockCheckOrder, setStockCheckOrder] = useState(null);
    
    const [selectedCompany, setSelectedCompany] = useState(() => {
        const saved = sessionStorage.getItem('selectedCompany');
        try {
            return saved ? JSON.parse(saved) : null;
        } catch (e) {
            return null;
        }
    });

    useEffect(() => {
        if (selectedCompany) {
            sessionStorage.setItem('selectedCompany', JSON.stringify(selectedCompany));
        } else {
            sessionStorage.removeItem('selectedCompany');
        }
    }, [selectedCompany]);
    const [loading, setLoading] = useState(true);
    const [supplierBranding, setSupplierBranding] = useState({ logoUrl: null, companyName: null });
    const [subscriptionPlan, setSubscriptionPlan] = useState('STANDARD'); // default optimistic
    // Dashboard order detail drawer state
    const [dashboardDrawerOrder, setDashboardDrawerOrder] = useState(null);
    const [dashboardDrawerLoading, setDashboardDrawerLoading] = useState(false);
    const [cancelModalOrder, setCancelModalOrder] = useState(null);
    const [cancelModalTransition, setCancelModalTransition] = useState(null);
    const [dashboardWorkflow, setDashboardWorkflow] = useState(null);
    const [ordersInitialTab, setOrdersInitialTab] = useState('new_order');

    useEffect(() => {
        // Re-fetch orders whenever the user navigates to the dashboard
        if (activeView !== 'dashboard') {
            setLoading(false);
            return;
        }

        const loadOrders = async () => {
            try {
                const data = await orderService.getOrders();
                setOrders(data);
            } catch (error) {
                console.error("Failed to load orders", error);
            } finally {
                setLoading(false);
            }
        };
        loadOrders();
    }, [activeView]);

    useEffect(() => {
        // Pre-fetch workflow + supplier branding on mount
        const loadDashboardWorkflow = async () => {
            try {
                let plan = 'BASIC';
                try {
                    const tenant = await getCurrentTenant();
                    plan = tenant.subscriptionPlan || 'BASIC';
                    setSubscriptionPlan(plan);
                    if (tenant.logoUrl || tenant.businessName) {
                        setSupplierBranding({ logoUrl: tenant.logoUrl, companyName: tenant.businessName });
                    }
                } catch (e) { /* ignore */ }
                const wf = await getWorkflowByPlan(plan);
                setDashboardWorkflow(wf);
            } catch (e) {
                console.warn('Dashboard workflow fetch failed:', e);
            }
        };
        loadDashboardWorkflow();
    }, []);

    /**
     * Open the detail drawer from Dashboard rows.
     * Fetches the enriched order (with items + correct id shape) from salesOrderApi.
     */
    const handleDashboardViewOrder = async (rawOrder) => {
        if (!rawOrder) return;
        setDashboardDrawerLoading(true);
        try {
            const result = await getAllOrders();
            const enriched = (result.orders || []).find(
                o => o.id === (rawOrder.publicId || rawOrder.id) ||
                     o.publicId === rawOrder.publicId ||
                     o.id === rawOrder.id
            );
            setDashboardDrawerOrder(enriched || rawOrder);
        } catch (e) {
            // Fallback: open with raw order — drawer handles missing fields gracefully
            setDashboardDrawerOrder(rawOrder);
        } finally {
            setDashboardDrawerLoading(false);
        }
    };

    const handleDashboardNavigateToTab = (tabId) => {
        setOrdersInitialTab(tabId);
        setActiveView('orders');
    };

    const getAvailableTransitionsForOrder = useCallback((order) => {
        if (!dashboardWorkflow || !order || !order.status) return [];

        const normalize = (r) => {
            if (!r) return 'USER';
            return normalizeRole(r) || 'USER';
        };

        const userRole = normalize(currentRole);

        return dashboardWorkflow.transitions.filter(t => {
            // SPECIAL CASE: Cancellation transition (to status CANCELLED)
            const isCancelTransition = t.toStatus?.statusCode === 'CANCELLED';
            if (isCancelTransition) {
                if (order.status !== 'CREATED') return false;
                const statusMatch = t.fromStatus.statusCode === order.status ||
                    (t.fromStatus.statusCode === 'DISPATCHED' && order.status === 'DESPATCHED');
                if (!statusMatch) return false;

                const isAssignedSalesperson = (order.assignedSalesPersonPublicId === (user?.publicId || user?.id)) ||
                    (order.delegatedSalesPersonPublicId === (user?.publicId || user?.id));
                const isManagerOrAdmin = ['SALES_MANAGER', 'TENANT_ADMIN', 'TENANT_OWNER', 'ADMIN', 'SYSTEM_ADMIN'].includes(userRole);
                const isSalesPersonAllowed = userRole === 'SALES_PERSON' && isAssignedSalesperson;
                return isManagerOrAdmin || isSalesPersonAllowed;
            }

            // SPECIAL CASE: Reactivate Order (CANCELLED -> CREATED)
            const isReactivateTransition = t.fromStatus.statusCode === 'CANCELLED' && t.toStatus.statusCode === 'CREATED';
            if (isReactivateTransition) {
                if (order.status !== 'CANCELLED') return false;
                
                const isCanceller = order.cancelledBy && user && order.cancelledBy === user.username;
                const isManagerOrAdmin = ['SALES_MANAGER', 'TENANT_ADMIN', 'TENANT_OWNER', 'ADMIN', 'SYSTEM_ADMIN'].includes(userRole);
                const isAssignedSalesperson = (order.assignedSalesPersonPublicId === (user?.publicId || user?.id)) ||
                    (order.delegatedSalesPersonPublicId === (user?.publicId || user?.id));
                const isSalesPersonAllowed = userRole === 'SALES_PERSON' && isAssignedSalesperson;
                return isCanceller || isManagerOrAdmin || isSalesPersonAllowed;
            }

            // Match status
            const statusMatch = t.fromStatus.statusCode === order.status ||
                (t.fromStatus.statusCode === 'DISPATCHED' && order.status === 'DESPATCHED');
            if (!statusMatch) return false;

            // Privileged roles see all, except warehouse actions if not a warehouse person
            const privilegedRoles = ['TENANT_OWNER', 'TENANT_ADMIN', 'SYSTEM_ADMIN'];
            if (privilegedRoles.includes(userRole)) {
                const actionName = (t.transitionName || t.label || t.name || t.action || '').toLowerCase();
                const isWarehouseSpecific = ((actionName.includes('revise') || actionName.includes('revision')) && !actionName.includes('resubmit') && !actionName.includes('accept')) || actionName.includes('reject');
                if (isWarehouseSpecific && userRole !== 'WAREHOUSE_PERSON') {
                    return false;
                }
                return true;
            }

            const isAllowedRole = t.allowedRoles.some(r => {
                const normR = normalize(r);
                if (userRole === 'SALES_MANAGER' && normR === 'SALES_PERSON') return true;
                return normR === userRole || normR === 'USER';
            });

            if (isAllowedRole) return true;

            // Assigned salesperson logic
            const isAssignedSalesperson = (order.assignedSalesPersonPublicId === (user?.publicId || user?.id)) ||
                (order.delegatedSalesPersonPublicId === (user?.publicId || user?.id));

            if (userRole === 'SALES_PERSON' && isAssignedSalesperson) {
                const actionName = (t.transitionName || t.label || t.name || t.action || '').toLowerCase();
                const toStatus = (t.toStatus?.statusCode || '').toLowerCase();
                const isMarkReadyAction = actionName.includes('ready') || actionName.includes('approve') || toStatus.includes('ready') || toStatus.includes('approved');
                const isInWarehouseStage = ['sent_to_warehouse', 'pending_warehouse', 'in_warehouse'].includes(order.status?.toLowerCase());

                if (isInWarehouseStage && isMarkReadyAction) return false;
                return true;
            }
            return false;
        });
    }, [dashboardWorkflow, currentRole, user]);


    const handleCancelSubmit = async (order, transition, payload) => {
        setCancelModalOrder(null);
        setCancelModalTransition(null);
        await handleDashboardDrawerTransition(order, transition, payload);
    };

    const handleDashboardDrawerTransition = async (order, transition, payload = {}) => {
        const isCancel = transition.toStatus?.statusCode === 'CANCELLED';
        if (isCancel && !payload.notes) {
            setCancelModalOrder(order);
            setCancelModalTransition(transition);
            return;
        }

        setDashboardDrawerLoading(true);
        try {
            const { executeTransition } = await import('../services/api/salesOrderApi');
            await executeTransition(order.id, transition.publicId || transition.id, payload);
            setDashboardDrawerOrder(null);
            const data = await orderService.getOrders();
            setOrders(data);
        } catch (err) {
            console.error('Action failed:', err);
            alert(`Action failed: ${err?.response?.data?.message || 'Please try again.'}`);
        } finally {
            setDashboardDrawerLoading(false);
        }
    };

    const handleStatusUpdate = async (orderId, newStatus, notes) => {
        try {
            await orderService.updateOrder(orderId, {
                status: newStatus,
                statusHistory: [
                    ...orders.find(o => o.id === orderId).statusHistory,
                    {
                        status: newStatus,
                        timestamp: new Date(),
                        user: currentRole,
                        notes
                    }
                ]
            });
            // Refresh orders
            const data = await orderService.getOrders();
            setOrders(data);
        } catch (error) {
            console.error("Failed to update status", error);
        }
    };


    const handleStockCheck = async (orderId, newStatus, itemStatuses, notes) => {
        try {
            // Need to merge item availability status
            const order = orders.find(o => o.id === orderId);
            const updatedItems = order.items.map((item, idx) => ({
                ...item,
                available: itemStatuses[idx].available
            }));

            await orderService.updateOrder(orderId, {
                status: newStatus,
                items: updatedItems,
                statusHistory: [
                    ...order.statusHistory,
                    {
                        status: newStatus,
                        timestamp: new Date(),
                        user: 'Tom Wilson', // Mock storekeeper
                        notes
                    }
                ]
            });
            const data = await orderService.getOrders();
            setOrders(data);
        } catch (error) {
            console.error("Failed to submit stock check", error);
        }
    };

    const renderContent = () => {
        const userOrders = orders;

        if (loading) return <div>Loading...</div>;

        switch (activeView) {
            case 'dashboard':
                if (currentRole === 'SYSTEM_ADMIN') {
                    return (
                        <SystemAdminDashboard 
                            onNavigate={(view, data) => {
                                if (data) setSelectedCompany(data);
                                setActiveView(view);
                            }} 
                        />
                    );
                }
                return <Dashboard role={currentRole} orders={userOrders} onViewOrder={handleDashboardViewOrder} onNavigateToTab={handleDashboardNavigateToTab} />;
            case 'orders':
                return <SalesOrdersView role={currentRole} onNavigate={setActiveView} initialTab={ordersInitialTab} />;
            case 'products':
                return <ProductsView />;
            case 'customers':
                return <CustomersView role={currentRole} />;
            case 'pos':
                return <PosView onNavigate={setActiveView} />;
            case 'staff':
                return <StaffView currentRole={currentRole} />;
            case 'settings':
                return <SettingsView />;
            case 'sales-orders':
                return <SalesOrdersView key="sales-orders" role={currentRole} onNavigate={setActiveView} initialTab={ordersInitialTab} />;
            case 'sales-completed':
                return <SalesOrdersView key="sales-completed" role={currentRole} onNavigate={setActiveView} initialTab="completed" />;
            case 'sales-cancelled':
                return <SalesOrdersView key="sales-cancelled" role={currentRole} onNavigate={setActiveView} initialTab="cancelled" />;
            case 'warehouse-orders':
                return <WarehouseRecentOrders standalone sortOrder="desc" />;
            case 'warehouse-confirmed':
                return <WarehouseConfirmedView />;
            case 'warehouse-dashboard':
                return <WarehouseManagementContainer currentRole={currentRole} initialSubView="dashboard" onViewChange={setActiveView} supplierLogoUrl={supplierBranding.logoUrl} companyName={supplierBranding.companyName} />;
            case 'loading-requests':
                return <WarehouseManagementContainer currentRole={currentRole} initialSubView="loading-requests" onViewChange={setActiveView} supplierLogoUrl={supplierBranding.logoUrl} companyName={supplierBranding.companyName} />;
            case 'offload-requests':
                return <WarehouseManagementContainer currentRole={currentRole} initialSubView="offload-requests" onViewChange={setActiveView} supplierLogoUrl={supplierBranding.logoUrl} companyName={supplierBranding.companyName} />;
            case 'transfer-history':
                return <WarehouseManagementContainer currentRole={currentRole} initialSubView="transfer-history" onViewChange={setActiveView} supplierLogoUrl={supplierBranding.logoUrl} companyName={supplierBranding.companyName} />;
            case 'analytics':
                if (currentRole === 'SYSTEM_ADMIN') {
                    return <SystemAnalyticsView />;
                }
                if (currentRole === 'SALES_PERSON') {
                    return <SalesPersonAnalyticsView />;
                }
                return <AnalyticsView />;
            case 'companies':
                return <CompaniesView onViewCompany={(company) => {
                    setSelectedCompany(company);
                    setActiveView('company-detail');
                }} />;
            case 'company-detail':
                return <CompanyDetailView
                    companyId={selectedCompany?.id}
                    onBack={() => {
                        setSelectedCompany(null);
                        setActiveView('companies');
                    }}
                />;
            case 'subscription-plans':
                return <SubscriptionPlansView />;
            case 'pending': // Storekeeper view
                // Reuse OrdersView but filtered, or a specific component. 
                // For now reuse OrdersView with filter
                return (
                    <OrdersView
                        orders={orders.filter(o => o.status === 'submitted_to_store')}
                        onView={setSelectedOrder}
                        onAction={setStockCheckOrder}
                        userRole={currentRole}
                    />
                );
            case 'sales-report':
                return <DailySalesReportView />;
            case 'sales-targets':
                return <SalesTargetsView />;
            case 'daily-reports':
                return <DailyReportsView 
                    role={currentRole}
                    supplierLogoUrl={supplierBranding.logoUrl} 
                    companyName={supplierBranding.companyName} 
                />;
            case 'operations-analytics':
                return <OperationsAnalyticsView role={currentRole} />;
            case 'profile':
                return <ProfileView currentRole={currentRole} />;
            default:
                return <div className="p-8">View {activeView} not implemented yet</div>;
        }
    };

    return (
        <div className="flex min-h-screen bg-[var(--color-bg-primary)] relative font-sans">
            <Sidebar
                currentRole={currentRole}
                availableRoles={availableRoles}
                onRoleChange={handleRoleChange}
                activeView={activeView}
                onViewChange={setActiveView}
                onLogout={handleLogout}
                subscriptionPlan={subscriptionPlan}
            />

            {/* Main Column */}
            <div className="flex-1 flex flex-col min-w-0 h-[100dvh]">
                <TopHeader 
                    supplierLogoUrl={supplierBranding.logoUrl}
                    companyName={supplierBranding.companyName}
                    currentRole={currentRole}
                    availableRoles={availableRoles}
                    onRoleChange={handleRoleChange}
                />

                {/* Main content area */}
                <main className={`flex-1 flex flex-col min-h-0 scroll-smooth ${
                    ['pos', 'products', 'customers', 'settings', 'staff', 'companies', 'company-detail', 'subscription-plans', 'orders', 'sales-orders', 'sales-completed', 'sales-cancelled', 'warehouse-orders', 'warehouse-confirmed', 'loading-requests', 'offload-requests', 'transfer-history', 'warehouse-dashboard', 'analytics', 'sales-report', 'daily-reports', 'operations-analytics', 'profile'].includes(activeView) 
                    ? 'overflow-hidden' 
                    : 'overflow-y-auto'
                } ${
                    ['pos', 'products', 'customers', 'settings', 'staff', 'companies', 'company-detail', 'subscription-plans', 'orders', 'sales-orders', 'sales-completed', 'sales-cancelled', 'warehouse-orders', 'warehouse-confirmed', 'loading-requests', 'offload-requests', 'transfer-history', 'warehouse-dashboard', 'analytics', 'sales-report', 'daily-reports', 'operations-analytics', 'profile'].includes(activeView)
                    ? 'p-0 pb-20 md:pb-0'
                    : activeView === 'dashboard'
                    ? 'p-4 md:p-6 pb-24 md:pb-6'
                    : 'p-2 md:p-3 pb-24 md:pb-3'
                }`}>
                    {renderContent()}
                </main>
            </div>

            <MobileNavbar
                currentRole={currentRole}
                activeView={activeView}
                onViewChange={setActiveView}
                onLogout={handleLogout}
                subscriptionPlan={subscriptionPlan}
            />

            {selectedOrder && (
                <OrderDetailsModal
                    order={selectedOrder}
                    onClose={() => setSelectedOrder(null)}
                    onStatusUpdate={handleStatusUpdate}
                    userRole={currentRole}
                />
            )}

            {stockCheckOrder && (
                <StockCheckModal
                    order={stockCheckOrder}
                    onClose={() => setStockCheckOrder(null)}
                    onSubmit={(id, status, items, notes) => {
                        handleStockCheck(id, status, items, notes);
                        setStockCheckOrder(null);
                    }}
                />
            )}

            {/* Dashboard-level Order Detail Drawer */}
            {dashboardDrawerLoading && (
                <div className="fixed inset-0 bg-gray-900/10 z-[199] flex items-center justify-center pointer-events-none">
                    <div className="bg-white rounded-2xl shadow-xl px-6 py-4 flex items-center gap-3 ring-1 ring-gray-100">
                        <div className="w-5 h-5 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                        <span className="text-sm font-bold text-gray-600">Loading order details...</span>
                    </div>
                </div>
            )}
            {dashboardDrawerOrder && (
                <OrderDetailDrawer
                    order={dashboardDrawerOrder}
                    transitions={getAvailableTransitionsForOrder(dashboardDrawerOrder)}
                    onTransition={handleDashboardDrawerTransition}
                    loadingTransition={dashboardDrawerLoading}
                    onClose={() => setDashboardDrawerOrder(null)}
                    role={currentRole}
                />
            )}

            <CancelOrderModal
                isOpen={!!cancelModalOrder}
                order={cancelModalOrder}
                transition={cancelModalTransition}
                onClose={() => {
                    setCancelModalOrder(null);
                    setCancelModalTransition(null);
                }}
                onSubmit={handleCancelSubmit}
                loading={dashboardDrawerLoading}
            />
        </div>

    );
};

export default AdminPage;
